import { useState, useEffect, useCallback } from "react";
import {
  TEAM_ROLES,
  TeamRole,
  hasAdminPrivileges,
} from "../../constants/roles";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Team, TeamMember } from "@/types";
import {
  getTeamMembers,
  addTeamMember,
  removeTeamMember,
  updateTeamMemberRole,
} from "@/api/teams";
import { ErrorDisplay } from "@/components/ui/error";
import { useAuth } from "@/providers/AuthProvider";
import { Loader } from "@/components/ui/loader";
import { TrashIcon, UserPlusIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface TeamMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  team: Team;
}

export function TeamMembersDialog({
  open,
  onOpenChange,
  team,
}: TeamMembersDialogProps) {
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState<TeamRole>(
    TEAM_ROLES.RECRUITER
  );
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState<string | null>(null);
  const [isUpdatingRole, setIsUpdatingRole] = useState<string | null>(null);

  // Fetch team members when dialog opens
  const fetchMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getTeamMembers(team.id);
      setMembers(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load team members"
      );
    } finally {
      setIsLoading(false);
    }
  }, [team]);

  useEffect(() => {
    if (open && team) {
      fetchMembers();
    }
  }, [open, team, fetchMembers]);

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMemberEmail.trim()) {
      setError("Email is required");
      return;
    }

    try {
      setIsAddingMember(true);
      setError(null);

      await addTeamMember(team.id, {
        email: newMemberEmail,
        role: newMemberRole,
      });

      // Reset form and refresh members list
      setNewMemberEmail("");
      setNewMemberRole(TEAM_ROLES.RECRUITER);
      await fetchMembers();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add team member"
      );
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      setIsRemovingMember(memberId);
      setError(null);

      await removeTeamMember(team.id, memberId);

      // Refresh members list
      await fetchMembers();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to remove team member"
      );
    } finally {
      setIsRemovingMember(null);
    }
  };

  const handleRoleChange = async (memberId: string, role: string) => {
    try {
      setIsUpdatingRole(memberId);
      setError(null);

      await updateTeamMemberRole(team.id, memberId, role);

      // Refresh members list
      await fetchMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update role");
    } finally {
      setIsUpdatingRole(null);
    }
  };

  const currentUserIsAdmin = members.some(
    (member) => member.user_id === user?.id && hasAdminPrivileges(member.role)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[650px]">
        <DialogHeader>
          <DialogTitle>Team members - {team.name}</DialogTitle>
          <DialogDescription>Manage members of this team.</DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          ) : (
            <>
              {members.length > 0 ? (
                <div className="border rounded-md mb-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {members.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell className="font-medium">
                            {member.email}
                            {member.user_id === user?.id && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                You
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {currentUserIsAdmin &&
                            member.user_id !== user?.id ? (
                              <Select
                                value={member.role}
                                onValueChange={(value) =>
                                  handleRoleChange(member.id, value)
                                }
                                disabled={isUpdatingRole === member.id}
                              >
                                <SelectTrigger className="w-[180px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value={TEAM_ROLES.OWNER}>
                                    Owner
                                  </SelectItem>
                                  <SelectItem value={TEAM_ROLES.HIRING_MANAGER}>
                                    Hiring manager
                                  </SelectItem>
                                  <SelectItem value={TEAM_ROLES.RECRUITER}>
                                    Recruiter
                                  </SelectItem>
                                  <SelectItem value={TEAM_ROLES.GUEST}>
                                    Guest
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="capitalize">{member.role}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {currentUserIsAdmin &&
                              member.user_id !== user?.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveMember(member.id)}
                                  disabled={isRemovingMember === member.id}
                                >
                                  <TrashIcon className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No team members found.
                </div>
              )}

              {currentUserIsAdmin && (
                <form onSubmit={handleAddMember} className="space-y-4">
                  <div className="text-sm font-medium mb-2">Add new member</div>
                  <div className="grid grid-cols-[1fr_150px_auto] gap-2">
                    <Input
                      placeholder="Email address"
                      value={newMemberEmail}
                      onChange={(e) => setNewMemberEmail(e.target.value)}
                      disabled={isAddingMember}
                    />
                    <Select
                      value={newMemberRole}
                      onValueChange={(value) =>
                        setNewMemberRole(value as TeamRole)
                      }
                      disabled={isAddingMember}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={TEAM_ROLES.OWNER}>Owner</SelectItem>
                        <SelectItem value={TEAM_ROLES.HIRING_MANAGER}>
                          Hiring manager
                        </SelectItem>
                        <SelectItem value={TEAM_ROLES.RECRUITER}>
                          Recruiter
                        </SelectItem>
                        <SelectItem value={TEAM_ROLES.GUEST}>Guest</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="submit"
                      disabled={isAddingMember}
                      isLoading={isAddingMember}
                    >
                      <UserPlusIcon className="h-4 w-4 mr-2" />
                      Add
                    </Button>
                  </div>
                </form>
              )}
            </>
          )}

          {error && <ErrorDisplay message={error} className="mt-4" />}
        </div>

        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
